import React from "react";
import { useAuth } from "contexts/api";
import { useHRData } from "contexts/app/hrData";
import { usePermissions } from "contexts/app/permissions";
import {
  Permission,
  PLANNING_EXCURSION_BOOKINGS_KEYS,
  PLANNING_FILTER_MENU_KEYS,
} from "api/zekay/permissions";
import { findGlobalUnavailabilityLeaveType } from "utils/planningLeaveHelpers";

export function usePlanningPermissions() {
  const { user } = useAuth();
  const { leaveTypes } = useHRData();
  const { hasPermission, hasAnyPermission } = usePermissions();

  const canAccessPlanning = hasPermission(Permission.PLANNING_ACCESS);
  const shouldLoadExcursionBookings = hasAnyPermission(PLANNING_EXCURSION_BOOKINGS_KEYS);
  const hasPlanningAllView = hasPermission(Permission.PLANNING_ALL_VIEW);
  const hasPlanningCompanyView = hasPermission(Permission.PLANNING_COMPANY_VIEW);
  const hasPlanningMeView = hasPermission(Permission.PLANNING_ME_VIEW);
  const showMeViewCompanyCartouche =
    hasPlanningMeView && !hasPlanningCompanyView && !hasPlanningAllView;
  const canShowPlanningFilters = hasAnyPermission(PLANNING_FILTER_MENU_KEYS);
  const canValidateAnyBooking = hasPermission(Permission.LEAVE_VALIDATE);

  const hasPlanningDataView = hasAnyPermission([
    Permission.PLANNING_ME_VIEW,
    Permission.PLANNING_COMPANY_VIEW,
    Permission.PLANNING_ALL_VIEW,
  ]);

  const globalUnavailabilityLeaveType = React.useMemo(
    () => findGlobalUnavailabilityLeaveType(leaveTypes),
    [leaveTypes],
  );

  // POST /leave/requests: leave.create OR (planning.access + me|company|all view + global indispo type) for non-employees.
  const canCreatePlanningGlobalIndispo =
    hasPermission(Permission.PLANNING_ACCESS) &&
    hasPlanningDataView &&
    globalUnavailabilityLeaveType != null &&
    user?.Role?.is_employee === false;

  const canCreateLeaveFromPlanning =
    hasPlanningDataView &&
    (user?.Role?.is_employee !== false || globalUnavailabilityLeaveType != null) &&
    (hasPermission(Permission.LEAVE_CREATE) || canCreatePlanningGlobalIndispo);

  return {
    canAccessPlanning,
    shouldLoadExcursionBookings,
    hasPlanningAllView,
    hasPlanningCompanyView,
    hasPlanningMeView,
    showMeViewCompanyCartouche,
    canShowPlanningFilters,
    canValidateAnyBooking,
    hasPlanningDataView,
    globalUnavailabilityLeaveType,
    canCreateLeaveFromPlanning,
  };
}
